import React, { useRef, useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import { shakeInvalidFields } from "../../../../ui-kit/kuika-form-page/form-shake";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { noop } from "../_layout/utils";
import { VariantGrid } from "../_layout/variant-grid";

type InputSize = "sm" | "md" | "lg";

const SIZES: InputSize[] = ["sm", "md", "lg"];

interface IStatefulInputProps {
  initial?: string;
  size?: InputSize;
  type?: "text" | "password" | "number" | "textarea";
  placeholder?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftUnit?: string;
  rightUnit?: string;
  disabled?: boolean;
  borderless?: boolean;
  rows?: number;
  min?: number;
  max?: number;
  precision?: number;
  allowClear?: boolean;
  readOnly?: boolean;
}

const StatefulInput: React.FC<IStatefulInputProps> = ({ initial = "", size = "md", type = "text", ...rest }) => {
  const [value, setValue] = useState<string>(initial);
  return (
    <KuikaInput
      value={value}
      size={size}
      type={type}
      onChange={(_e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, v: string | number) =>
        setValue(String(v))
      }
      {...rest}
    />
  );
};

// Save with the field empty: it turns invalid, shakes once and takes focus; save again, it shakes again.
const SubmitShakeDemo: React.FC = (): JSX.Element => {
  const rootRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const isInvalid: boolean = submitted && value.trim() === "";

  const handleSave = (): void => {
    setSubmitted(true);
    // A frame later, so the error class this save turns on is on the page first.
    requestAnimationFrame((): void => {
      if (rootRef.current) {
        shakeInvalidFields(rootRef.current);
      }
    });
  };

  return (
    <div ref={rootRef} style={{ display: "flex", gap: "var(--gap-s)", alignItems: "center" }}>
      <KuikaInput
        placeholder="Required"
        value={value}
        error={isInvalid}
        onChange={(_e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, v: string | number): void =>
          setValue(String(v))
        }
        style={{ width: 240 }}
      />
      <KuikaButton type="primary" size="md" onClick={handleSave}>
        Save
      </KuikaButton>
    </div>
  );
};

export const InputStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Input"
      componentName="KuikaInput"
      sourcePath="ui-kit/kuika-input"
      description="One input primitive covers text, number, password and textarea modes — with size, icon, unit and borderless affordances. Sizes stand on the control scale (the select's rungs); focus shows the kit ring."
    >
      <VariantGrid
        label="size"
        caption="24 · 32 · 40px, border included — the same rungs as the select"
        cols={SIZES}
        cellMinWidth={200}
        render={(size: string): React.ReactNode => (
          <StatefulInput placeholder="email@kuika.com" size={size as InputSize} initial="" />
        )}
      />
      <VariantGrid
        label="state · size"
        rows={["default", "filled", "disabled"]}
        cols={SIZES}
        cellMinWidth={180}
        render={(state: string, size: string): React.ReactNode => (
          <StatefulInput
            placeholder="email@kuika.com"
            size={size as InputSize}
            initial={state === "filled" ? "furkan.ceylan@kuika.com" : ""}
            disabled={state === "disabled"}
          />
        )}
      />
      <SpecRow label="Error" caption="error — red frame and text">
        <KuikaInput value="not-an-email" error onChange={noop} />
        <KuikaInput placeholder="email@kuika.com" error onChange={noop} />
      </SpecRow>
      <SpecRow label="Submit shake" caption="shakeInvalidFields — save with the field empty">
        <SubmitShakeDemo />
      </SpecRow>
      <SpecRow label="With left and right icon">
        <StatefulInput placeholder="Search components" leftIcon={<KuikaSvg name="search" width={14} height={14} />} />
        <StatefulInput
          placeholder="Search components"
          leftIcon={<KuikaSvg name="search" width={14} height={14} />}
          rightIcon={<KuikaSvg name="x" width={12} height={12} />}
        />
      </SpecRow>
      <SpecRow label="With units" caption="leftUnit / rightUnit slots">
        <StatefulInput placeholder="0.00" leftUnit="$" />
        <StatefulInput placeholder="0" rightUnit="%" type="number" />
        <StatefulInput placeholder="example" leftUnit="https://" rightUnit=".com" />
      </SpecRow>
      <SpecRow label="Borderless" caption="for inline editing inside data cells">
        <KuikaInput value="Inline value" borderless onChange={noop} />
      </SpecRow>
      <SpecRow label="Type variants" caption="text · password · number" align="start">
        <StatefulInput placeholder="text" type="text" />
        <StatefulInput initial="********" type="password" />
        <StatefulInput placeholder="0" type="number" />
      </SpecRow>
      <SpecRow
        label="Clearing"
        caption="allowClear — shown only while the field holds something, and only where someone can type"
      >
        <StatefulInput initial="Something to clear" allowClear />
        <StatefulInput placeholder="Nothing to clear yet" allowClear />
        <StatefulInput initial="Read only" allowClear readOnly />
        <StatefulInput initial="Disabled" allowClear disabled />
      </SpecRow>
      <SpecRow
        label="Bounded numbers"
        caption="min / max clamp on the way out · precision sets the step · shift+arrow moves by ten"
        align="start"
      >
        <StatefulInput type="number" placeholder="1 – 10" min={1} max={10} />
        <StatefulInput type="number" placeholder="two decimals" precision={2} />
      </SpecRow>
      <SpecRow label="Textarea" align="start">
        <KuikaInput
          type="textarea"
          rows={4}
          placeholder="Describe your screen — Kuika will generate it."
          onChange={noop}
          style={{ width: 480 }}
        />
      </SpecRow>
    </ShowcaseFrame>
  );
};
